/**
 * `formatTimeForScreenReader` is a utility function that formats the time remaining
 * until another parent request can be sent for screen readers. It takes the hours
 * and minutes digits and returns a string that describes the time remaining in a way
 * that is accessible to screen readers.
 * @param hoursDigits {number} - The number of hours remaining.
 * @param minutesDigits {number} - The number of minutes remaining.
 * @return {string} - A formatted string for screen readers.
 */
export const formatTimeForScreenReader = (hoursDigits: number, minutesDigits: number): string => {
  const parts = []
  if (hoursDigits > 0) parts.push(`${hoursDigits} ${hoursDigits === 1 ? 'hour' : 'hours'}`)
  if (minutesDigits > 0)
    parts.push(`${minutesDigits} ${minutesDigits === 1 ? 'minute' : 'minutes'}`)
  return parts.join(', ') + ' remaining until another parent request can be sent'
}

/**
 * `shouldAnnounce` is a utility function that determines whether the countdown
 * should announce the time remaining for screen readers.
 * @param announceInterval {number} - The interval in seconds at which to announce.
 * @param lastAnnouncement {number} - The last second at which an announcement was made.
 * @param epochMilliseconds {number} - The current epoch milliseconds.
 * @return {boolean} - True if an announcement should be made, false otherwise.
 */
export const shouldAnnounce = (
  announceInterval: number,
  lastAnnouncement: number,
  epochMilliseconds: number,
) => {
  const currentSeconds = Math.floor(epochMilliseconds / 1000)
  return (
    currentSeconds % announceInterval === 0 &&
    currentSeconds !== lastAnnouncement &&
    currentSeconds > 0
  )
}
